import { useEffect, useId, useState } from "react";
import type { ChangeEvent } from "react";
import { cx } from "./cx";
import styles from "./FileInput.module.css";

export type FileInputProps = {
  /** Types MIME acceptés, ex. `["image/png"]`. Vérifiés AVANT tout envoi. */
  accept: readonly string[];
  /** Taille maximale en octets, vérifiée avant tout envoi. */
  maxBytes: number;
  value: File | null;
  /**
   * Appelé au choix d'un fichier. `error` non nul quand le fichier est refusé
   * côté client ; `file` est alors `null`.
   */
  onChange: (file: File | null, error: string | null) => void;
  /** Libellé du bouton. */
  label?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
};

/**
 * Sélecteur de fichier image, avec aperçu.
 *
 * L'`<input type="file">` reste dans le document (pour le clavier et les
 * lecteurs d'écran) mais est visuellement caché ; c'est le `<label>` qui porte
 * l'apparence du bouton. Le type et la taille sont vérifiés ici : refuser un
 * fichier de 3 Mo avant de l'envoyer épargne un aller-retour, mais le serveur
 * revérifie, par le contenu et non par ce que le navigateur déclare.
 */
export function FileInput({
  accept,
  maxBytes,
  value,
  onChange,
  label = "Choisir un fichier",
  disabled,
  id,
  className,
}: FileInputProps) {
  const idGenere = useId();
  const idInput = id ?? idGenere;
  const [apercu, setApercu] = useState<string | null>(null);

  // L'URL d'objet est révoquée quand le fichier change ou que le composant
  // part : sinon chaque aperçu reste en mémoire jusqu'à la fermeture de l'onglet.
  useEffect(() => {
    if (value === null) {
      setApercu(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setApercu(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  function choisir(e: ChangeEvent<HTMLInputElement>) {
    const fichier = e.target.files?.[0] ?? null;
    // Permet de re-choisir le même fichier après un refus.
    e.target.value = "";
    if (fichier === null) {
      onChange(null, null);
      return;
    }
    if (!accept.includes(fichier.type)) {
      onChange(null, "Ce type de fichier n'est pas accepté : PNG, JPEG ou WebP.");
      return;
    }
    if (fichier.size > maxBytes) {
      onChange(null, `Fichier trop volumineux : ${Math.round(fichier.size / 1024)} Kio, maximum ${Math.round(maxBytes / 1024)} Kio.`);
      return;
    }
    onChange(fichier, null);
  }

  return (
    <div className={cx(styles.zone, className)}>
      {apercu !== null && value !== null && (
        <img className={styles.apercu} src={apercu} alt={`Aperçu de ${value.name}`} />
      )}
      <div className={styles.controle}>
        <input
          id={idInput}
          type="file"
          className={styles.input}
          accept={accept.join(",")}
          onChange={choisir}
          disabled={disabled}
        />
        <label htmlFor={idInput} className={cx(styles.bouton, disabled && styles.inactif)}>
          {label}
        </label>
        <span className={styles.nom}>
          {value ? `${value.name} (${Math.round(value.size / 1024)} Kio)` : "Aucun fichier choisi"}
        </span>
      </div>
    </div>
  );
}
